import React, { useRef, useMemo, useImperativeHandle, forwardRef, useCallback } from 'react';
import JoditEditor from 'jodit-react';
import { Label } from '../input-text/input.styled';
import { ActionButton } from '../vertical-dot/popper';

interface JoditRichType {
  name: string;
  onChange: (value: string) => void;
  value: string;
  error?: any;
  touched?: any;
  label?: string;
  variables?: string[];
  disabled?: boolean;
  variableTypes?: boolean;
}

export interface EditorRef {
  insertText: (textToInsert: string) => void;
}

const JoditRichTextEditor = forwardRef<EditorRef, JoditRichType>(
  ({ onChange, value, error, touched, label, variables, disabled, variableTypes }, ref) => {
    const editorRef = useRef<any>(null);
    const joditInstanceRef = useRef<any>(null);
    const latestContentRef = useRef<string>(value);

    const config = useMemo(
      () => ({
        readonly: disabled ?? false,
        height: 495,
        minHeight: 495,
        toolbarSticky: true,
        toolbarAdaptive: false,
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        buttons: [
          'bold',
          'italic',
          'underline',
          '|',
          'ul',
          'ol',
          '|',
          'font',
          'fontsize',
          'paragraph',
          '|',
          'table',
          'link',
          '|',
          'align',
          'indent',
          'outdent',
          '|',
          'eraser',
          'undo',
          'redo',
          '|',
          'source',
        ],
        removeButtons: ['about'],
        disablePlugins: ['powered-by-jodit'],
        tabIndex: 1,
        useSplitMode: false,
        enter: 'p' as const,
        defaultActionOnPaste: 'insert_as_html' as const,
        uploader: { insertImageAsBase64URI: true },
        events: {
          afterInit: (editor: any) => {
            joditInstanceRef.current = editor;
            editor.e.on('keydown', (e: KeyboardEvent) => {
              if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                editor.s.insertHTML('&nbsp;&nbsp;&nbsp;&nbsp;');
                return false;
              }
            });
          },
        },
      }),
      [disabled]
    );

    const insertText = useCallback((textToInsert: string) => {
      const editor = joditInstanceRef.current;
      if (editor) {
        editor.s.focus();
        editor.s.insertHTML(textToInsert);
        onChange(editor.value);
      }
    }, [onChange]);

    useImperativeHandle(ref, () => ({
      insertText,
    }));

    const handleBlur = useCallback((newContent: string) => {
      latestContentRef.current = newContent;
      onChange(newContent);
    }, [onChange]);

    const handleChange = useCallback((newContent: string) => {
      latestContentRef.current = newContent;
    }, []);

    return (
      <div>
        {label && <Label>{label || 'Description'}</Label>}
        {variables && variables.length > 0 && (
          <div className='mb-2' style={{ display: 'inline-block' }}>
            <ActionButton
              as='Button'
              buttonTitle='Insert Variables'
              icon='add'
              hideMenuOnActionClick
              actions={variables.map((variable) => ({
                action: variable,
                onClick: () => {
                  setTimeout(() => {
                    if (variableTypes) {
                      insertText(`[${variable}]`);
                    } else {
                      insertText(`{{${variable}}}`);
                    }
                  }, 0);
                },
              }))}
              doubleTable={true}
            />
          </div>
        )}
        <div
          className={`${Boolean(error) && Boolean(touched) ? 'border border-red-700 rounded' : ''}`}>
          <JoditEditor
            ref={editorRef}
            value={value}
            config={config}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </div>
        {Boolean(error) && Boolean(touched) && <div className='field-error-message'>{error}</div>}
      </div>
    );
  }
);

export default JoditRichTextEditor;
